<template>
  <window :window-id="windowId">
    <window-tab :title="windowTitle">
      <div class="form-group form-row">
        <div class="col-12">
          <b-form-file @change="onFileChange" class="w-100"
                       :placeholder="fileName || 'Choose file...'"
                       choose-label="..."
                       accept=".csv, .ods, .xls, .xlsx"></b-form-file>
        </div>
      </div>
      <div class="form-group form-row">
        <div class="col-12" style="text-align: center;">--- or ---</div>
      </div>
      <div class="form-group form-row">
        <div class="col-12">
          <div class="input-group">
            <input type="text" class="form-control" placeholder="URL..." v-model="localDataUrl"/>
            <div class="input-group-append">
              <button type="button" class="btn btn-primary" @click="fetchUrl">Fetch</button>
            </div>
          </div>
        </div>
      </div>
      <div class="form-group form-row" v-if="datasetId">
        <label :for="safeId('refetchSecs')" class="col-sm-8 col-form-label">Automatic refresh (seconds)</label>
        <div class="col-sm-4">
          <input type="number" class="form-control" :id="safeId('refetchSecs')" v-model.lazy="refetchSecs">
        </div>
      </div>
      <div class="alert alert-danger" v-if="errorMessage">
        <p><b>Error:</b> {{ errorMessage }}</p>
      </div>
      <div class="form-group form-row" v-if="datasetId">
        <div class="col-12">
          <div class="d-flex flex-row justify-content-end">
            <button type="button" class="btn btn-outline-primary" @click="downloadExcelFile">Download Excel file
            </button>
          </div>
        </div>
      </div>
      <dataset-table :dataset-id="datasetId"></dataset-table>
    </window-tab>
    <window-tab title="Settings" icon="cog">
      <dataset-settings :window-id="windowId" :dataset-id="datasetId"></dataset-settings>
    </window-tab>
  </window>
</template>

<script>
import XLSX from 'xlsx'
import { uuid } from '../lib'
import { windowPropsMixin } from '../mixins'
import * as mutation from '../store/mutation-types'
import DatasetSettings from './dataset-settings.vue'
import DatasetTable from './dataset-table.vue'

export default {
  name: 'dataset',
  components: {DatasetSettings, DatasetTable},
  mixins: [windowPropsMixin],
  data () {
    return {
      errorMessage: null,
      localDataUrl: null,
      refetchSecs: 0,
      timeoutId: null
    }
  },
  watch: {
    refetchSecs: function (val) {
      this.setTimeout()
    },
    dataset: {
      handler: function (val) {
        if (val.fileName !== this.localDataUrl) {
          this.localDataUrl = val.fileName
          this.fetchUrl()
        }
      },
      deep: true
    }
  },
  beforeDestroy () {
    this.clearTimeout()
  },
  created () {
    this.localDataUrl = this.dataUrl
    if (this.dataUrl) {
      this.fetchUrl()
    }
  },
  computed: {
    datasetId () {
      return this.$store.getters.windowData(this.windowId).contentId
    },
    dataset () {
      return this.$store.getters.dataset(this.datasetId) || {}
    },
    title () {
      return this.dataset.title || 'Unnamed data source'
    },
    fileName () {
      return ('' + this.dataset.fileName).indexOf('://') >= 0 ? '' : this.dataset.fileName
    },
    dataUrl: {
      get () {
        return this.localDataUrl || (('' + this.dataset.fileName).indexOf('://') >= 0 ? this.dataset.fileName : '')
      },
      set (val) {
        this.localDataUrl = val
      }
    }
  },
  methods: {
    downloadExcelFile () {
      const data = this.$store.getters.dataset(this.datasetId) || {}
      console.log('data', data.records || [])
      const sheet = XLSX.utils.json_to_sheet(data.records || [])
      const book = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(book, sheet)
      XLSX.writeFile(book, data.name + '.xlsx')
    },
    /* See https://github.com/SheetJS/js-xlsx/blob/master/demos/vue/SheetJS-vue.js */
    onFileChange (evt) {
      let file
      const files = evt.target.files

      if (!files || files.length === 0) return

      file = files[0]

      const reader = new FileReader()
      reader.onload = function (e) {
        // pre-process data
        let binary = ''
        const bytes = new Uint8Array(e.target.result)
        const length = bytes.byteLength
        for (let i = 0; i < length; i++) {
          binary += String.fromCharCode(bytes[i])
        }

        /* read workbook */
        const wb = XLSX.read(binary, {type: 'binary'})

        /* grab first sheet */
        const wsname = wb.SheetNames[0]
        const ws = wb.Sheets[wsname]

        const records = XLSX.utils.sheet_to_json(ws)
        this.saveRecords(file.name, records)
      }.bind(this)

      reader.readAsArrayBuffer(file)
    },
    fetchUrl () {
      if (!this.dataUrl) {
        return
      }
      console.info('Fetching ' + this.dataUrl)
      window.clearTimeout(this.timeoutId)
      this.errorMessage = null
      const request = new XMLHttpRequest()
      request.open('GET', this.dataUrl, true)
      request.onload = function (event) {
        if (!event.target) {
          this.errorMessage = 'Unknown error'
          // Set timeout to refresh data
          this.setTimeout()
          return
        }

        if (event.target.status !== 200) {
          this.errorMessage = event.target.statusText
          // Set timeout to refresh data
          this.setTimeout()
          return
        }

        const response = event.target.response
        const contentType = event.target.getResponseHeader('Content-Type') || ''

        let records = null
        if (contentType.split(';')[0] === 'application/json') {
          records = JSON.parse(response)
        } else {
          /* read workbook from string (your existing response) */
          const wb = XLSX.read(response, {type: 'string'})

          /* get first worksheet */
          const wsname = wb.SheetNames[0]
          const ws = wb.Sheets[wsname]

          /* read first row (headers) */
          const data = XLSX.utils.sheet_to_json(ws, { header: 1 })
          if (data.length > 0) {
            const headers = data[0].map(h =>
              typeof h === 'string'
                ? h
                  .replace(/ä/g, 'ae')
                  .replace(/ö/g, 'oe')
                  .replace(/ü/g, 'ue')
                  .replace(/Ä/g, 'Ae')
                  .replace(/Ö/g, 'Oe')
                  .replace(/Ü/g, 'Ue')
                  .replace(/ß/g, 'ss')
                : h
            )

            /* write headers back into row 1 */
            XLSX.utils.sheet_add_aoa(ws, [headers], { origin: 'A1' })
          }

          /* convert to JSON */
          records = XLSX.utils.sheet_to_json(ws, {defval: null})
          console.log('records', records)
        }
        this.saveRecords(this.localDataUrl, records)

        // Set timeout to refresh data
        this.setTimeout()
      }.bind(this)
      request.onerror = function (event) {
        console.log(event)
        this.errorMessage = 'Network error'
        // Set timeout to refresh data
        this.setTimeout()
      }.bind(this)
      request.send()
    },
    setTimeout () {
      this.clearTimeout()
      if (this.refetchSecs > 0) {
        this.timeoutId = window.setTimeout(this.fetchUrl.bind(this), this.refetchSecs * 1000)
      }
    },
    clearTimeout () {
      if (this.timeoutId) {
        window.clearTimeout(this.timeoutId)
        this.timeoutId = null
      }
    },
    saveRecords (fileName, records) {
      this.$store.commit(mutation.DATASET_UPDATE_META, {
        datasetId: this.datasetId,
        fileName: fileName,
        emit: true
      })
      this.$store.commit(mutation.DATASET_UPDATE_RECORDS, {
        datasetId: this.datasetId,
        columns: records[0] ? Object.keys(records[0]) : [],
        records: records.map((record, index) => {
          record.recordId = record.id || uuid()
          record.SORT_ORDER = index + 1
          return record
        }),
        emit: true
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/dataset.vue